import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  DatatableCell,
  Spinner,
} from '@monksflow/monks-ui';
import { RefreshCwIcon, Trash2Icon } from 'lucide-react';
import type { AnalysisListItem, AnalysisStatus } from '@/entities/analysis';
import { UserAvatar } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import { AnimatedTableRow } from '@/shared/ui/animations';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { useTranslation } from 'react-i18next';

interface AnalysisTableRowProps {
  analysis: AnalysisListItem;
  index: number;
  onRetry: (id: string) => void;
  onDelete: (id: string, options?: { onSettled?: () => void }) => void;
  isRetrying: boolean;
  isDeleting: boolean;
}

const STATUS_BADGE_VARIANT = {
  pending: 'secondary',
  running: 'warning',
  completed: 'positive',
  failed: 'destructive',
} as const satisfies Record<AnalysisStatus, 'secondary' | 'positive' | 'destructive' | 'warning'>;

export function AnalysisTableRow(props: AnalysisTableRowProps) {
  const { analysis, index, onRetry, onDelete, isRetrying, isDeleting } = props;
  const { t } = useTranslation('analysis');

  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isGenerating = analysis.status === 'pending' || analysis.status === 'running';
  const isFailed = analysis.status === 'failed';
  const isCompleted = analysis.status === 'completed';
  const isMutating = isRetrying || isDeleting;

  const statusLabel = isGenerating
    ? t('analysis.analysisTable.generating')
    : isFailed
      ? t('analysis.analysisTable.failed')
      : t('analysis.analysisTable.completed');

  const handleRowClick = () => {
    if (isCompleted && !deleteOpen) void navigate(ROUTES.IQ_ANALYSIS(analysis.id));
  };

  const handleConfirmDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onDelete(analysis.id, { onSettled: () => setDeleteOpen(false) });
  };

  return (
    <>
      <AnimatedTableRow
        index={index}
        variant="datatable"
        interactive={isCompleted}
        data-testid={`analysis-row-${analysis.id}`}
        className="h-16 text-sm font-normal text-foreground"
        onClick={handleRowClick}
      >
        <DatatableCell>
          <div className="min-w-0">
            <div className="text-sm font-medium whitespace-normal text-foreground">
              {analysis.name}
            </div>
            <p className="whitespace-normal text-xs text-muted-foreground">{analysis.query}</p>
          </div>
        </DatatableCell>

        <DatatableCell>
          <Badge
            data-testid="analysis-status-badge"
            variant={STATUS_BADGE_VARIANT[analysis.status]}
            className="gap-1.5 font-normal"
            size="sm"
          >
            {isGenerating ? <Spinner size="sm" /> : null}
            {statusLabel}
          </Badge>
        </DatatableCell>

        <DatatableCell>
          <div className="flex items-center gap-2">
            <UserAvatar userId={analysis.createdBy} />
            <span className="text-xs text-muted-foreground">
              {formatRelativeDate(analysis.createdAt)}
            </span>
          </div>
        </DatatableCell>

        <DatatableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            {isFailed && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('analysis.analysisTable.retryAnalysis')}
                title={t('analysis.analysisTable.retryAnalysis')}
                disabled={isMutating}
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry(analysis.id);
                }}
              >
                <RefreshCwIcon className="size-4 text-foreground-alt" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('analysis.analysisTable.deleteReport')}
              title={t('analysis.analysisTable.deleteReport')}
              disabled={isMutating}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteOpen(true);
              }}
            >
              <Trash2Icon className="size-4 text-foreground-alt" />
            </Button>
          </div>
        </DatatableCell>
      </AnimatedTableRow>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('analysis.analysisCard.deleteAnalysis')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('analysis.analysisCard.areYouSureYouWantToDelete', {
                name: analysis.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('analysis.analysisCard.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('analysis.analysisCard.deleting') : t('analysis.analysisCard.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
